import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowBack, Add, Delete, Warning, CheckCircle, Fullscreen, FullscreenExit } from "@mui/icons-material";
import api from '../../services/api';
import styles from '../../assets/styles/LayoutShared.module.css';
import { useFecharComEsc } from '../../utils/useFecharComEsc';

// ===========================================================================
// Tipos — espelham o formato de dados_revisados do Node
// (inteagro-node/src/pesca/importacaoFichaTypes.ts)
// ===========================================================================

type ApresentacaoValor = 'bruto' | 'eviscerado' | 'filé';
type EstadoPeso = 'informado' | 'vazio' | 'zero' | 'ilegivel';

interface CapturaRevisada {
  id_estavel: string;
  dia: number | null;
  uid_pesca_especie: string | null;
  especie_texto: string;
  peso_kg: number | null;
  estado_peso: EstadoPeso;
  apresentacao: ApresentacaoValor;
  origem_apresentacao: string | null;
  posicao_referencia: string;
  pendencia: string | null;
  removida: boolean;
}

interface ExcecaoDia {
  descricao_local?: string | null;
  uid_pesca_apetrechos?: string[];
  num_pescadores?: number | null;
  num_embarcacoes?: number | null;
}

interface DadosGeraisRevisados {
  uid_pesca_projeto: string | null;
  mes: number | null;
  ano: number | null;
  descricao_local: string | null;
  uid_pesca_apetrechos: string[];
  num_pescadores: number | null;
  num_embarcacoes: number | null;
  excecoesPorDia: Record<string, ExcecaoDia>;
}

interface DadosRevisados {
  cabecalho: DadosGeraisRevisados;
  capturas: CapturaRevisada[];
  observacoes_gerais: string | null;
  descartes: string | null;
  pescador_nome_ficha: string | null;
}

interface Pendencia {
  tipo: string;
  descricao: string;
  posicao_referencia?: string | null;
  id_estavel_captura?: string | null;
}

interface Conflito {
  dia: number;
  uid_pesca_registro_existente: string;
}

const NOMES_MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const ORIGEM_LABEL: Record<string, string> = {
  captura: 'indicado na própria captura', especie: 'indicado na coluna da espécie',
  geral_ficha: 'anotação geral da ficha', formulario: 'instrução impressa no formulário',
  historico: 'sugerido pelo histórico do pescador', padrao: 'padrão (sem evidência) — confira',
};

let seq = 0;
const novaCaptura = (dia: number | null = null): CapturaRevisada => ({
  id_estavel: `nova-${seq++}`, dia, uid_pesca_especie: null, especie_texto: '',
  peso_kg: null, estado_peso: 'vazio', apresentacao: 'bruto', origem_apresentacao: null,
  posicao_referencia: '', pendencia: null, removida: false,
});

const ImportarFichaCampo: React.FC = () => {
  const { uid: uidPescador } = useParams<{ uid: string }>();
  const navigate = useNavigate();

  const [nomePescador, setNomePescador] = useState('');
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [fase, setFase] = useState<'upload' | 'processando' | 'erro' | 'revisao' | 'confirmando' | 'sucesso'>('upload');
  const [erroUpload, setErroUpload] = useState('');
  const [uidImportacao, setUidImportacao] = useState<string | null>(null);
  const [urlArquivo, setUrlArquivo] = useState('');
  const [imagemFicha, setImagemFicha] = useState('');
  const [importacoesDuplicadas, setImportacoesDuplicadas] = useState<any[]>([]);
  const [matrizTelaCheia, setMatrizTelaCheia] = useState(false);
  useFecharComEsc(matrizTelaCheia, () => setMatrizTelaCheia(false));
  // Pendências da extração que NÃO estão ligadas a uma captura específica
  // (id_estavel_captura nulo) — não têm como ser recalculadas a partir da
  // matriz, então continuam mostradas como vieram do backend. As que SÃO
  // ligadas a uma captura entram em pendenciasLive (recalculada a cada
  // edição, ver mais abaixo) pra nunca "travar" mostrando um problema já
  // corrigido.
  const [pendenciasExtracao, setPendenciasExtracao] = useState<Pendencia[]>([]);

  const [dados, setDados] = useState<DadosRevisados | null>(null);
  const [conflitos, setConflitos] = useState<Conflito[]>([]);
  const [resolucaoConflitos, setResolucaoConflitos] = useState<Record<string, 'ignorar' | 'criar_mesmo_assim'>>({});
  const [capturasPendentesBloqueio, setCapturasPendentesBloqueio] = useState<string[]>([]);
  const [erroConfirmar, setErroConfirmar] = useState('');
  const [resultadoConfirmacao, setResultadoConfirmacao] = useState<{ quantidade: number; uids_registros_criados: string[] } | null>(null);

  const [projetos, setProjetos] = useState<any[]>([]);
  const [especies, setEspecies] = useState<any[]>([]);
  const [apetrechosDisponiveis, setApetrechosDisponiveis] = useState<any[]>([]);

  const pollingRef = useRef<any>(null);
  const salvamentoDebounce = useRef<any>(null);
  const enviandoConfirmacaoRef = useRef(false);

  useEffect(() => {
    api.get('/pesca/projetos').then((res) => setProjetos(res.data.data));
    api.get('/pesca/apetrechos').then((res) => setApetrechosDisponiveis(res.data.data));
    if (uidPescador) {
      api.get(`/pesca/pescadores/${uidPescador}`).then((res) => setNomePescador(res.data.data?.nome || ''));
    }
  }, [uidPescador]);

  // Catálogo de espécies contextual ao projeto (SYNCORP-912, Task 6) — o
  // projeto só é escolhido durante a revisão (dados.cabecalho.uid_pesca_projeto),
  // então refaz a busca sempre que ele muda, igual ao RegistroForm/WhatsApp/
  // ficha de campo no backend (mesmo catálogo, mesma prioridade/fallback).
  const uidProjetoFicha = dados?.cabecalho.uid_pesca_projeto || '';
  useEffect(() => {
    const params = new URLSearchParams();
    if (uidProjetoFicha) params.set('uid_pesca_projeto', uidProjetoFicha);
    api.get(`/pesca/especies/catalogo?${params.toString()}`).then((res) => setEspecies(res.data.data));
  }, [uidProjetoFicha]);

  useEffect(() => () => { if (pollingRef.current) clearInterval(pollingRef.current); }, []);

  // Busca a imagem da ficha autenticada. Em produção (GCS) url_arquivo já é
  // uma URL assinada pública — usada direto. Em dev/on-prem sem GCS ela cai
  // na nossa própria rota /pesca/importacao-ficha/arquivo-local/..., que
  // exige o Bearer token; um <img src>/<a href> puro nunca manda esse
  // header, então a imagem quebrava e o "abrir em nova aba" caía na tela
  // crua de erro '{"message":"Token não informado"}'. Buscando o binário
  // pela instância autenticada do axios e servindo como blob: resolve os
  // dois casos sem tocar no comportamento (já público) do modo GCS.
  useEffect(() => {
    if (!urlArquivo) {
      setImagemFicha('');
      return;
    }
    if (!urlArquivo.includes('/pesca/importacao-ficha/arquivo-local/')) {
      setImagemFicha(urlArquivo);
      return;
    }
    let objectUrl = '';
    api.get(urlArquivo, { responseType: 'blob' })
      .then((res) => {
        objectUrl = URL.createObjectURL(res.data);
        setImagemFicha(objectUrl);
      })
      .catch(() => setImagemFicha(''));
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [urlArquivo]);

  // Auto-salva o progresso da revisão (debounced) — preserva as correções
  // se a confirmação falhar ou a página recarregar antes de confirmar.
  useEffect(() => {
    if (fase !== 'revisao' || !dados || !uidImportacao) return;
    if (salvamentoDebounce.current) clearTimeout(salvamentoDebounce.current);
    salvamentoDebounce.current = setTimeout(() => {
      api.put(`/pesca/importacao-ficha/${uidImportacao}/revisao`, { dadosRevisados: dados }).catch(() => {
        // Falha ao auto-salvar não trava a revisão — só perde o "seguro" daquela edição específica.
      });
    }, 800);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dados]);

  const handleUpload = async () => {
    if (!arquivo || !uidPescador) return;
    setErroUpload('');
    setFase('processando');
    try {
      const formData = new FormData();
      formData.append('uid_pesca_pescador', uidPescador);
      formData.append('arquivo', arquivo);
      const res = await api.post('/pesca/importacao-ficha', formData);
      setUidImportacao(res.data.data.uid_pesca_importacao_ficha);
      setImportacoesDuplicadas(res.data.data.importacoes_duplicadas || []);
      iniciarPolling(res.data.data.uid_pesca_importacao_ficha);
    } catch (err: any) {
      setErroUpload(err?.response?.data?.message || 'Erro ao enviar o arquivo.');
      setFase('upload');
    }
  };

  const iniciarPolling = (uid: string) => {
    pollingRef.current = setInterval(async () => {
      try {
        const res = await api.get(`/pesca/importacao-ficha/${uid}`);
        const importacao = res.data.data;
        if (importacao.status === 'processando') return;

        clearInterval(pollingRef.current);
        setUrlArquivo(importacao.url_arquivo);

        if (importacao.status === 'erro_extracao') {
          setErroUpload(importacao.erro_mensagem || 'Falha ao processar a ficha.');
          setFase('erro');
          return;
        }

        setDados(importacao.dados_revisados);
        setPendenciasExtracao((importacao.pendencias || []).filter((p: Pendencia) => !p.id_estavel_captura));
        setFase('revisao');
      } catch {
        // rede instável — próximo tick tenta de novo
      }
    }, 3000);
  };

  const tentarNovamente = () => {
    setArquivo(null);
    setErroUpload('');
    setUidImportacao(null);
    setFase('upload');
  };

  // ── Edição da matriz ───────────────────────────────────────────────────

  const atualizarCabecalho = (patch: Partial<DadosGeraisRevisados>) => {
    setDados((prev) => (prev ? { ...prev, cabecalho: { ...prev.cabecalho, ...patch } } : prev));
  };

  const atualizarCaptura = (id_estavel: string, patch: Partial<CapturaRevisada>) => {
    setDados((prev) => prev ? {
      ...prev,
      capturas: prev.capturas.map((c) => c.id_estavel === id_estavel ? { ...c, ...patch, origem_apresentacao: patch.apresentacao !== undefined ? null : c.origem_apresentacao } : c),
    } : prev);
  };

  const removerCaptura = (id_estavel: string) => {
    atualizarCaptura(id_estavel, { removida: true });
  };

  const adicionarCapturaNaCelula = (dia: number, uid_pesca_especie: string, especie_texto: string) => {
    setDados((prev) => {
      if (!prev) return prev;
      const nova = { ...novaCaptura(dia), uid_pesca_especie, especie_texto, estado_peso: 'informado' as EstadoPeso, peso_kg: 0 };
      return { ...prev, capturas: [...prev.capturas, nova] };
    });
  };

  const [novoDia, setNovoDia] = useState('');
  const [novaEspecieUid, setNovaEspecieUid] = useState('');
  const adicionarNovaCaptura = () => {
    if (!novoDia || !novaEspecieUid) return;
    const especie = especies.find((e) => e.uid_pesca_especie === novaEspecieUid);
    adicionarCapturaNaCelula(Number(novoDia), novaEspecieUid, especie?.nome_popular || '');
    setNovoDia('');
    setNovaEspecieUid('');
  };

  const toggleApetrechoCabecalho = (uid: string) => {
    atualizarCabecalho({
      uid_pesca_apetrechos: dados!.cabecalho.uid_pesca_apetrechos.includes(uid)
        ? dados!.cabecalho.uid_pesca_apetrechos.filter((x) => x !== uid)
        : [...dados!.cabecalho.uid_pesca_apetrechos, uid],
    });
  };

  // ── Derivações pra montar a matriz dia × espécie ───────────────────────

  const capturasAtivas = (dados?.capturas || []).filter((c) => !c.removida);
  const capturasResolvidas = capturasAtivas.filter((c) => c.uid_pesca_especie);
  const capturasPendentes = capturasAtivas.filter((c) => !c.uid_pesca_especie);

  const dias = [...new Set(capturasResolvidas.map((c) => c.dia).filter((d): d is number => d != null))].sort((a, b) => a - b);
  const colunasEspecie = [...new Map(capturasResolvidas.map((c) => [c.uid_pesca_especie!, c.especie_texto])).entries()];

  const totalPorDia = (dia: number) =>
    capturasResolvidas.filter((c) => c.dia === dia && c.estado_peso === 'informado').reduce((acc, c) => acc + (c.peso_kg || 0), 0);
  const totalPorEspecie = (uid: string) =>
    capturasResolvidas.filter((c) => c.uid_pesca_especie === uid && c.estado_peso === 'informado').reduce((acc, c) => acc + (c.peso_kg || 0), 0);

  const registrosAConferir = dias.filter((dia) =>
    capturasResolvidas.some((c) => c.dia === dia && c.estado_peso === 'informado' && (c.peso_kg || 0) > 0)
  ).length;

  const temPendenciaBloqueante = capturasPendentes.length > 0
    || capturasAtivas.some((c) => c.estado_peso === 'ilegivel')
    || !dados?.cabecalho.uid_pesca_projeto
    || !dados?.cabecalho.mes || !dados?.cabecalho.ano;

  // Espécies não identificadas AGRUPADAS pelo texto original — uma ficha
  // com uma coluna inteira não reconhecida (ex: "RAIL") gera uma captura
  // pendente POR DIA com dado naquela coluna; associar uma por uma seria
  // muito repetitivo e dava a impressão de que a correção "não pegava".
  // Associar aqui resolve TODAS as ocorrências daquele texto de uma vez.
  const gruposEspeciesPendentes = [...new Map(
    capturasPendentes.map((c) => [c.especie_texto, { texto: c.especie_texto, qtd: capturasPendentes.filter((x) => x.especie_texto === c.especie_texto).length }])
  ).values()];

  const associarEspecieEmMassa = (especieTextoOriginal: string, uidEspecie: string) => {
    const especie = especies.find((e) => e.uid_pesca_especie === uidEspecie);
    setDados((prev) => prev ? {
      ...prev,
      capturas: prev.capturas.map((c) =>
        !c.uid_pesca_especie && c.especie_texto === especieTextoOriginal
          ? { ...c, uid_pesca_especie: uidEspecie, especie_texto: especie?.nome_popular || c.especie_texto, pendencia: null }
          : c
      ),
    } : prev);
  };

  const removerEspecieEmMassa = (especieTextoOriginal: string) => {
    setDados((prev) => prev ? {
      ...prev,
      capturas: prev.capturas.map((c) =>
        !c.uid_pesca_especie && c.especie_texto === especieTextoOriginal ? { ...c, removida: true } : c
      ),
    } : prev);
  };

  // Pendência ATUAL de uma captura, recalculada a partir do estado vivo —
  // nunca do texto congelado que veio da extração (aquele texto nunca
  // mudava depois de corrigido em tela, dando a impressão de bug: a
  // pendência "voltava" mesmo já resolvida).
  const problemaAtual = (c: CapturaRevisada): string | null => {
    if (!c.uid_pesca_especie) return `Dia ${c.dia ?? '?'}: espécie "${c.especie_texto}" não identificada — associe abaixo.`;
    if (c.estado_peso === 'ilegivel') return `Dia ${c.dia ?? '?'}, ${c.especie_texto}: peso ilegível — edite o valor na célula.`;
    return null;
  };

  const pendenciasLive: string[] = [];
  gruposEspeciesPendentes.forEach((g) => pendenciasLive.push(`Espécie "${g.texto}" não identificada no catálogo (${g.qtd} ocorrência${g.qtd === 1 ? '' : 's'}) — associe na lista abaixo.`));
  capturasAtivas.filter((c) => c.uid_pesca_especie && c.estado_peso === 'ilegivel').forEach((c) => {
    const item = problemaAtual(c);
    if (item) pendenciasLive.push(item);
  });
  if (!dados?.cabecalho.uid_pesca_projeto) pendenciasLive.push('Selecione o projeto.');
  if (!dados?.cabecalho.mes || !dados?.cabecalho.ano) pendenciasLive.push('Preencha o mês e o ano.');

  // ── Confirmação ─────────────────────────────────────────────────────────

  const handleConfirmar = async () => {
    if (!dados || !uidImportacao) return;
    // Trava contra clique duplo — sem isso, um duplo-clique rápido dispara
    // DUAS chamadas antes do estado "confirmando" desabilitar o botão de
    // verdade, e as duas respostas de conflito (idênticas) acabavam
    // aparecendo como se a lista tivesse "duplicado" na tela.
    if (enviandoConfirmacaoRef.current) return;
    enviandoConfirmacaoRef.current = true;

    try {
      // Clicar em Salvar de novo com conflitos AINDA sem escolha (usuário
      // não marcou "ignorar" nem "confirmar mesmo assim" pra algum dia)
      // pergunta uma vez só se quer confirmar TODOS mesmo assim, em vez de
      // simplesmente falhar de novo mostrando a mesma lista de novo.
      let resolucaoParaEnviar = resolucaoConflitos;
      const conflitosSemResolucao = conflitos.filter((c) => !resolucaoConflitos[String(c.dia)]);
      if (conflitosSemResolucao.length > 0) {
        const confirmarTodos = window.confirm(
          `Ainda há ${conflitosSemResolucao.length} dia${conflitosSemResolucao.length === 1 ? '' : 's'} com registro já existente sem escolha. Confirmar a criação mesmo assim para todos eles?`
        );
        if (!confirmarTodos) return;
        resolucaoParaEnviar = { ...resolucaoConflitos };
        conflitosSemResolucao.forEach((c) => { resolucaoParaEnviar[String(c.dia)] = 'criar_mesmo_assim'; });
        setResolucaoConflitos(resolucaoParaEnviar);
      }

      setErroConfirmar('');
      // Limpa a lista antiga ANTES de tentar de novo — nunca deixa a
      // próxima resposta (ou a ausência de uma nova, em caso de sucesso)
      // se somar visualmente à lista da tentativa anterior.
      setConflitos([]);
      setFase('confirmando');
      const res = await api.post(`/pesca/importacao-ficha/${uidImportacao}/confirmar`, {
        dadosRevisados: dados,
        resolucaoConflitos: resolucaoParaEnviar,
      });
      setResultadoConfirmacao(res.data.data);
      setFase('sucesso');
    } catch (err: any) {
      const resp = err?.response?.data;
      setFase('revisao');
      if (resp?.detalhes?.conflitos) {
        setConflitos(resp.detalhes.conflitos);
        setErroConfirmar(resp.message);
      } else if (resp?.detalhes?.capturas_pendentes) {
        setCapturasPendentesBloqueio(resp.detalhes.capturas_pendentes);
        setErroConfirmar(resp.message);
      } else {
        setErroConfirmar(resp?.message || 'Erro ao confirmar a importação.');
      }
    } finally {
      enviandoConfirmacaoRef.current = false;
    }
  };

  // ===========================================================================
  // Render
  // ===========================================================================

  return (
    <div className={styles.container} style={{ maxWidth: '1400px' }}>
      <div className={styles.headerTitle}>
        <h1 className={styles.greenTitle}>Importar Ficha de Campo</h1>
        <div className={styles.actionsHeader}>
          <p style={{ alignSelf: 'center', fontSize: '0.9rem', color: '#666', marginRight: '10px' }}>
            Pescador: <strong>{nomePescador || '...'}</strong>
          </p>
          <button className={styles.blueBtn} onClick={() => navigate(-1)} title="Voltar">
            <ArrowBack />
          </button>
        </div>
      </div>

      {fase === 'upload' && (
        <div style={{ background: '#fff', borderRadius: '10px', padding: '30px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', maxWidth: '520px' }}>
          <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '16px' }}>
            Envie uma foto (JPEG ou PNG) da ficha de campo preenchida à mão. O pescador já está fixado pelo cadastro
            de origem — se o nome na ficha for diferente, você será avisado na revisão.
          </p>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={fileLabelStyle}>
              {arquivo ? arquivo.name : 'Escolher arquivo...'}
              <input type="file" accept="image/jpeg,image/png" style={{ display: 'none' }} onChange={(e) => setArquivo(e.target.files?.[0] || null)} />
            </label>
            <button style={primaryBtnStyle} disabled={!arquivo} onClick={handleUpload}>
              Importar
            </button>
          </div>
          {erroUpload && <p style={{ color: '#f75a68', fontSize: '0.85rem', marginTop: '10px' }}>{erroUpload}</p>}
        </div>
      )}

      {fase === 'processando' && (
        <div style={{ background: '#fff', borderRadius: '10px', padding: '40px', textAlign: 'center', color: '#666' }}>
          <p>Processando a ficha (leitura da imagem + interpretação)... isso pode levar até um minuto.</p>
        </div>
      )}

      {fase === 'erro' && (
        <div style={{ background: '#fff', borderRadius: '10px', padding: '30px', maxWidth: '520px' }}>
          <p style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', color: '#f75a68' }}>
            <Warning fontSize="small" /> {erroUpload}
          </p>
          <button style={primaryBtnStyle} onClick={tentarNovamente}>Tentar novamente</button>
        </div>
      )}

      {(fase === 'revisao' || fase === 'confirmando') && dados && (
        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* Ficha original */}
          <div style={{ flex: '0 0 320px', position: 'sticky', top: '10px' }}>
            <div style={{ background: '#fff', borderRadius: '10px', padding: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
              <p style={{ fontWeight: 600, fontSize: '0.82rem', margin: '0 0 8px', color: '#666' }}>Ficha original</p>
              {imagemFicha && (
                <a href={imagemFicha} target="_blank" rel="noopener noreferrer">
                  <img src={imagemFicha} alt="Ficha de campo" style={{ width: '100%', borderRadius: '8px', cursor: 'zoom-in' }} />
                </a>
              )}
              <p style={{ fontSize: '0.72rem', color: '#999', marginTop: '6px' }}>Clique pra ampliar em outra aba.</p>
            </div>

            {importacoesDuplicadas.length > 0 && (
              <div style={{ ...avisoBoxStyle, marginTop: '10px' }}>
                <Warning fontSize="small" /> Esse mesmo arquivo já foi importado antes ({importacoesDuplicadas.length}x). Confira se não é duplicado.
              </div>
            )}

            {pendenciasLive.length > 0 && (
              <div style={{ background: '#fff', borderRadius: '10px', padding: '12px', marginTop: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                <p style={{ fontWeight: 600, fontSize: '0.82rem', margin: '0 0 8px', color: '#b56a1f' }}>Pendências ({pendenciasLive.length})</p>
                <p style={{ fontSize: '0.72rem', color: '#999', margin: '0 0 8px' }}>Atualiza conforme você corrige a ficha abaixo.</p>
                {pendenciasLive.map((texto, idx) => (
                  <p key={idx} style={{ fontSize: '0.78rem', color: '#666', margin: '0 0 6px' }}>• {texto}</p>
                ))}
              </div>
            )}

            {pendenciasExtracao.length > 0 && (
              <div style={{ background: '#fff', borderRadius: '10px', padding: '12px', marginTop: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                <p style={{ fontWeight: 600, fontSize: '0.82rem', margin: '0 0 8px', color: '#666' }}>Observações gerais da leitura</p>
                {pendenciasExtracao.map((p, idx) => (
                  <p key={idx} style={{ fontSize: '0.78rem', color: '#666', margin: '0 0 6px' }}>• {p.descricao}</p>
                ))}
              </div>
            )}
          </div>

          {/* Matriz de revisão */}
          <div style={{ flex: '1 1 700px', minWidth: 0 }}>
            {/* Dados gerais */}
            <div style={{ background: '#fff', borderRadius: '10px', padding: '16px', marginBottom: '14px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
                <div style={{ flex: '1 1 200px' }}>
                  <label style={labelStyle}>Projeto *</label>
                  <select style={inputStyle} value={dados.cabecalho.uid_pesca_projeto || ''} onChange={(e) => atualizarCabecalho({ uid_pesca_projeto: e.target.value || null })}>
                    <option value="">Selecione...</option>
                    {projetos.map((p) => <option key={p.uid_pesca_projeto} value={p.uid_pesca_projeto}>{p.nome}</option>)}
                  </select>
                </div>
                <div style={{ flex: '1 1 140px' }}>
                  <label style={labelStyle}>Mês *</label>
                  <select style={inputStyle} value={dados.cabecalho.mes || ''} onChange={(e) => atualizarCabecalho({ mes: e.target.value ? Number(e.target.value) : null })}>
                    <option value="">Selecione...</option>
                    {NOMES_MESES.map((nome, idx) => <option key={nome} value={idx + 1}>{nome}</option>)}
                  </select>
                </div>
                <div style={{ flex: '0 0 100px' }}>
                  <label style={labelStyle}>Ano *</label>
                  <input type="number" style={inputStyle} value={dados.cabecalho.ano || ''} onChange={(e) => atualizarCabecalho({ ano: e.target.value ? Number(e.target.value) : null })} />
                </div>
              </div>

              {dados.pescador_nome_ficha && (
                <p style={{ fontSize: '0.78rem', color: '#b56a1f', margin: '0 0 10px' }}>
                  <Warning fontSize="inherit" /> Nome na ficha: "{dados.pescador_nome_ficha}" — confira se bate com o cadastro ({nomePescador}).
                </p>
              )}

              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
                <div style={{ flex: '1 1 200px' }}>
                  <label style={labelStyle}>Local (aplicado a todos os dias, exceto onde houver exceção)</label>
                  <input style={inputStyle} value={dados.cabecalho.descricao_local || ''} onChange={(e) => atualizarCabecalho({ descricao_local: e.target.value })} />
                </div>
                <div style={{ flex: '0 0 120px' }}>
                  <label style={labelStyle}>Nº Pescadores</label>
                  <input type="number" style={inputStyle} value={dados.cabecalho.num_pescadores ?? ''} onChange={(e) => atualizarCabecalho({ num_pescadores: e.target.value ? Number(e.target.value) : null })} />
                </div>
                <div style={{ flex: '0 0 120px' }}>
                  <label style={labelStyle}>Nº Embarcações</label>
                  <input type="number" style={inputStyle} value={dados.cabecalho.num_embarcacoes ?? ''} onChange={(e) => atualizarCabecalho({ num_embarcacoes: e.target.value ? Number(e.target.value) : null })} />
                </div>
              </div>

              <label style={labelStyle}>Artes de Pesca</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px' }}>
                {apetrechosDisponiveis.map((a) => (
                  <label key={a.uid_pesca_apetrecho} style={chipStyle(dados.cabecalho.uid_pesca_apetrechos.includes(a.uid_pesca_apetrecho))}>
                    <input type="checkbox" style={{ marginRight: '6px' }} checked={dados.cabecalho.uid_pesca_apetrechos.includes(a.uid_pesca_apetrecho)} onChange={() => toggleApetrechoCabecalho(a.uid_pesca_apetrecho)} />
                    {a.nome}
                  </label>
                ))}
              </div>
            </div>

            {/* Matriz dia x espécie — em tela cheia quando matrizTelaCheia (backdrop
                escurece o resto da página; a própria matriz vira um cartão fixo
                cobrindo quase todo o viewport). É o MESMO bloco de JSX nos dois
                casos (só muda o estilo do container) pra edição dentro da tela
                cheia continuar atualizando o mesmo estado normalmente. */}
            {matrizTelaCheia && <div style={telaCheiaBackdropStyle} onClick={() => setMatrizTelaCheia(false)} />}
            <div style={matrizTelaCheia ? matrizCardTelaCheiaStyle : matrizCardNormalStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px', marginBottom: '10px' }}>
                <p style={{ fontWeight: 600, fontSize: '0.85rem', margin: 0 }}>
                  Capturas por dia — legenda: <strong>I</strong> Inteiro/Bruto · <strong>E</strong> Eviscerado · <strong>F</strong> Filé
                </p>
                <button
                  type="button"
                  style={iconToggleBtnStyle}
                  title={matrizTelaCheia ? 'Sair da tela cheia' : 'Expandir a matriz em tela cheia'}
                  onClick={() => setMatrizTelaCheia((v) => !v)}
                >
                  {matrizTelaCheia ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
                </button>
              </div>

              {dias.length === 0 ? (
                <p style={{ color: '#999', fontSize: '0.85rem' }}>Nenhuma captura com espécie identificada ainda.</p>
              ) : (
                // Rolagem própria (dias/linhas passam, cabeçalho de espécie
                // fica fixo no topo) — numa ficha com muitos dias, sem isso
                // era fácil perder de vista qual coluna/espécie se estava
                // editando ao rolar pra baixo.
                <div style={matrizTelaCheia ? tabelaScrollTelaCheiaStyle : tabelaScrollNormalStyle}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                    <thead>
                      <tr>
                        <th style={thFixoStyle}>Dia</th>
                        {colunasEspecie.map(([uid, nome]) => <th key={uid} style={thFixoStyle}>{nome}</th>)}
                        <th style={thFixoStyle}>Total do dia</th>
                      </tr>
                    </thead>
                    <tbody>
                    {dias.map((dia) => (
                      <tr key={dia}>
                        <td style={{ ...tdStyle, fontWeight: 600 }}>{dia}</td>
                        {colunasEspecie.map(([uidEspecie]) => {
                          const itensCelula = capturasResolvidas.filter((c) => c.dia === dia && c.uid_pesca_especie === uidEspecie);
                          return (
                            <td key={uidEspecie} style={tdStyle}>
                              {itensCelula.map((c) => (
                                <CelulaCaptura key={c.id_estavel} captura={c} onChange={(patch) => atualizarCaptura(c.id_estavel, patch)} onRemover={() => removerCaptura(c.id_estavel)} destacada={capturasPendentesBloqueio.includes(c.id_estavel)} />
                              ))}
                              <button
                                type="button"
                                title="Adicionar outra captura dessa espécie nesse dia (ex: apresentação diferente)"
                                style={addCelulaBtnStyle}
                                onClick={() => {
                                  const nome = colunasEspecie.find(([u]) => u === uidEspecie)?.[1] || '';
                                  adicionarCapturaNaCelula(dia, uidEspecie, nome);
                                }}
                              >
                                +
                              </button>
                            </td>
                          );
                        })}
                        <td style={{ ...tdStyle, fontWeight: 600, color: 'var(--green-700)' }}>{totalPorDia(dia).toFixed(2)} kg</td>
                      </tr>
                    ))}
                      <tr>
                        <td style={{ ...tdStyle, fontWeight: 600 }}>Total espécie</td>
                        {colunasEspecie.map(([uid]) => <td key={uid} style={{ ...tdStyle, fontWeight: 600 }}>{totalPorEspecie(uid).toFixed(2)} kg</td>)}
                        <td style={tdStyle} />
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              <div style={{ display: 'flex', gap: '8px', marginTop: '14px', alignItems: 'center', flexWrap: 'wrap' }}>
                <input type="number" placeholder="Dia" min={1} max={31} style={{ ...inputStyle, width: '80px' }} value={novoDia} onChange={(e) => setNovoDia(e.target.value)} />
                <select style={{ ...inputStyle, width: '220px' }} value={novaEspecieUid} onChange={(e) => setNovaEspecieUid(e.target.value)}>
                  <option value="">Espécie...</option>
                  {especies.map((e) => <option key={e.uid_pesca_especie} value={e.uid_pesca_especie}>{e.nome_popular}</option>)}
                </select>
                <button type="button" style={secondaryBtnStyle} onClick={adicionarNovaCaptura}>
                  <Add fontSize="small" /> Adicionar captura
                </button>
              </div>
            </div>

            {/* Espécies pendentes — agrupadas pelo texto original lido na ficha.
                Uma coluna inteira não reconhecida gera 1 captura pendente POR
                DIA com dado nela; associar aqui corrige TODAS de uma vez (ver
                associarEspecieEmMassa), em vez de precisar repetir a escolha
                dia a dia. */}
            {gruposEspeciesPendentes.length > 0 && (
              <div style={{ background: '#fff', borderRadius: '10px', padding: '16px', marginBottom: '14px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', border: '1px solid #f8ecdc' }}>
                <p style={{ fontWeight: 600, fontSize: '0.85rem', margin: '0 0 10px', color: '#b56a1f' }}>
                  Espécies não identificadas ({capturasPendentes.length}) — resolva ou remova antes de confirmar
                </p>
                {gruposEspeciesPendentes.map((g) => (
                  <div key={g.texto} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.8rem', fontStyle: 'italic' }}>"{g.texto}"</span>
                    <span style={{ fontSize: '0.75rem', color: '#999' }}>({g.qtd} ocorrência{g.qtd === 1 ? '' : 's'})</span>
                    <select
                      style={{ ...inputStyle, width: '200px' }}
                      value=""
                      onChange={(e) => e.target.value && associarEspecieEmMassa(g.texto, e.target.value)}
                    >
                      <option value="">Associar todas a...</option>
                      {especies.map((e) => <option key={e.uid_pesca_especie} value={e.uid_pesca_especie}>{e.nome_popular}</option>)}
                    </select>
                    <Delete fontSize="small" style={{ color: '#f75a68', cursor: 'pointer' }} titleAccess="Remover todas as ocorrências" onClick={() => removerEspecieEmMassa(g.texto)} />
                  </div>
                ))}
              </div>
            )}

            {/* != null (não !== '') de propósito — se o usuário apagar o
                texto todo, o campo (agora vazio) tem que continuar visível
                e editável, não desaparecer só porque ficou "" e virou
                falsy. Só não aparece quando a extração nunca teve nada. */}
            {(dados.observacoes_gerais != null || dados.descartes) && (
              <div style={{ background: '#fff', borderRadius: '10px', padding: '16px', marginBottom: '14px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                {/* Observação era só texto read-only — se a IA leu errado
                    não tinha como corrigir antes de confirmar, e o registro
                    ia pro banco com a observação errada. Agora é editável;
                    o que o usuário digitar aqui é exatamente o que vai pro
                    registro ao confirmar (dados.observacoes_gerais já é o
                    campo que a confirmação usa direto). */}
                {dados.observacoes_gerais != null && (
                  <div style={{ marginBottom: dados.descartes ? '10px' : 0 }}>
                    <label htmlFor="ficha-observacoes-gerais" style={labelStyle}>Observações</label>
                    <textarea
                      id="ficha-observacoes-gerais"
                      style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }}
                      value={dados.observacoes_gerais}
                      onChange={(e) => setDados((prev) => prev ? { ...prev, observacoes_gerais: e.target.value } : prev)}
                    />
                  </div>
                )}
                {dados.descartes && <p style={{ fontSize: '0.82rem', margin: 0 }}><strong>Descartes:</strong> {dados.descartes}</p>}
              </div>
            )}

            {/* Conflitos — dedupe por dia como segurança extra (nunca
                mostra o mesmo dia duas vezes) e ordenado numericamente —
                a ordem que chega do backend é a ordem de inserção no
                banco, não a ordem dos dias. */}
            {conflitos.length > 0 && (
              <div style={{ ...avisoBoxStyle, marginBottom: '14px', flexDirection: 'column', alignItems: 'stretch' }}>
                <p style={{ margin: '0 0 8px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Warning fontSize="small" /> Já existe registro pra esses dias:
                </p>
                {[...new Map(conflitos.map((c) => [c.dia, c])).values()].sort((a, b) => a.dia - b.dia).map((c) => (
                  <div key={c.dia} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '6px' }}>
                    <span style={{ fontSize: '0.82rem', minWidth: '60px' }}>Dia {c.dia}</span>
                    <button type="button" style={smallBtnStyle(resolucaoConflitos[String(c.dia)] === 'ignorar')} onClick={() => setResolucaoConflitos((prev) => ({ ...prev, [String(c.dia)]: 'ignorar' }))}>Ignorar esse dia</button>
                    <button type="button" style={smallBtnStyle(resolucaoConflitos[String(c.dia)] === 'criar_mesmo_assim')} onClick={() => setResolucaoConflitos((prev) => ({ ...prev, [String(c.dia)]: 'criar_mesmo_assim' }))}>Confirmar mesmo assim</button>
                  </div>
                ))}
              </div>
            )}

            {erroConfirmar && <p style={{ color: '#f75a68', fontSize: '0.85rem', marginBottom: '10px' }}>{erroConfirmar}</p>}

            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
              <button
                type="button"
                style={{ ...primaryBtnStyle, opacity: temPendenciaBloqueante || fase === 'confirmando' ? 0.5 : 1 }}
                disabled={temPendenciaBloqueante || fase === 'confirmando'}
                onClick={handleConfirmar}
              >
                {fase === 'confirmando' ? 'Salvando...' : `Salvar ${registrosAConferir} registro${registrosAConferir === 1 ? '' : 's'} de pesca`}
              </button>
              {/* O botão desabilitado sem explicação nenhuma foi justamente a
                  principal reclamação — "não consigo entender o que impede a
                  habilitação". Esse texto aparece bem embaixo do botão (onde
                  o olho já está) em vez de só na lista de pendências lá em
                  cima, fácil de passar batido. */}
              {temPendenciaBloqueante && fase !== 'confirmando' && pendenciasLive.length > 0 && (
                <small style={{ color: '#b56a1f', fontSize: '0.76rem', textAlign: 'right', maxWidth: '360px' }}>
                  Ainda há {pendenciasLive.length} pendência{pendenciasLive.length === 1 ? '' : 's'} pra resolver antes de salvar.
                </small>
              )}
            </div>
          </div>
        </div>
      )}

      {fase === 'sucesso' && resultadoConfirmacao && (
        <div style={{ background: '#fff', borderRadius: '10px', padding: '30px', maxWidth: '520px' }}>
          <p style={{ display: 'flex', gap: '8px', alignItems: 'center', color: 'var(--green-700, #00b37e)', fontWeight: 600, fontSize: '1rem' }}>
            <CheckCircle /> {resultadoConfirmacao.quantidade} registro{resultadoConfirmacao.quantidade === 1 ? '' : 's'} de pesca criado{resultadoConfirmacao.quantidade === 1 ? '' : 's'} com sucesso.
          </p>
          <button style={{ ...primaryBtnStyle, marginTop: '10px' }} onClick={() => navigate(`/admin/pescadores/view/${uidPescador}`)}>
            Voltar pro cadastro do pescador
          </button>
        </div>
      )}
    </div>
  );
};

// ===========================================================================
// Subcomponente: uma captura dentro de uma célula da matriz
// ===========================================================================

const CelulaCaptura: React.FC<{
  captura: CapturaRevisada;
  onChange: (patch: Partial<CapturaRevisada>) => void;
  onRemover: () => void;
  destacada: boolean;
}> = ({ captura, onChange, onRemover, destacada }) => {
  const estadoPlaceholder: Record<EstadoPeso, string> = { informado: '', vazio: '', zero: 'zero', ilegivel: 'ilegível' };
  // "Tem problema" é recalculado do estado ATUAL (não do texto congelado
  // captura.pendencia vindo da extração) — senão a célula continuava
  // marcada em laranja pra sempre, mesmo já corrigida.
  const temProblema = !captura.uid_pesca_especie || captura.estado_peso === 'ilegivel';

  // Antes, uma célula sem estado_peso==='informado' (vazio/zero/ilegível)
  // virava só um <span> read-only — não tinha COMO digitar um valor nela,
  // o que travava a importação pra sempre em qualquer ficha com uma
  // célula ilegível (o botão de confirmar nunca habilitava e não tinha
  // pista de como resolver). Agora o campo é sempre editável; digitar um
  // número resolve a pendência, e esvaziar volta a ser "vazio".
  const handlePesoChange = (valor: string) => {
    if (valor.trim() === '') {
      onChange({ peso_kg: null, estado_peso: 'vazio' });
      return;
    }
    const numero = Number(valor);
    onChange({ peso_kg: Number.isNaN(numero) ? null : numero, estado_peso: 'informado' });
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '4px', padding: '4px', marginBottom: '4px',
      borderRadius: '6px', border: destacada ? '1.5px solid #f75a68' : (temProblema ? '1.5px solid #e6a75f' : '1px solid transparent'),
      background: temProblema || destacada ? '#fff8ee' : 'transparent',
    }}>
      <input
        type="number" min="0" step="0.01"
        style={{ width: '68px', padding: '2px 4px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '0.8rem' }}
        value={captura.peso_kg ?? ''}
        placeholder={estadoPlaceholder[captura.estado_peso]}
        onChange={(e) => handlePesoChange(e.target.value)}
        title={captura.estado_peso === 'ilegivel' ? 'Peso ilegível na ficha original — confira e digite o valor correto.' : (captura.origem_apresentacao ? ORIGEM_LABEL[captura.origem_apresentacao] || '' : 'apresentação escolhida manualmente')}
      />
      {(['bruto', 'eviscerado', 'filé'] as ApresentacaoValor[]).map((ap) => (
        <label key={ap} style={{ display: 'flex', alignItems: 'center', fontSize: '0.72rem', cursor: 'pointer' }} title={ap}>
          <input type="radio" checked={captura.apresentacao === ap} onChange={() => onChange({ apresentacao: ap })} style={{ marginRight: '2px' }} />
          {ap === 'bruto' ? 'I' : ap === 'eviscerado' ? 'E' : 'F'}
        </label>
      ))}
      <Delete fontSize="inherit" style={{ color: '#f75a68', cursor: 'pointer', flexShrink: 0 }} onClick={onRemover} />
    </div>
  );
};

// ===========================================================================
// Estilos
// ===========================================================================

const fileLabelStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '9px 16px', borderRadius: '20px',
  border: '1px solid #ddd', background: '#fafafa', color: '#444', fontSize: '0.85rem', cursor: 'pointer',
};
const labelStyle: React.CSSProperties = { display: 'block', fontSize: '0.78rem', color: '#666', marginBottom: '4px' };
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #ddd', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
};
const primaryBtnStyle: React.CSSProperties = {
  padding: '10px 22px', borderRadius: '20px', border: 'none', background: '#03c9fe', color: '#fff', cursor: 'pointer', fontWeight: 600,
};
const secondaryBtnStyle: React.CSSProperties = {
  padding: '8px 16px', borderRadius: '20px', border: '1px solid #ddd', background: '#fff', color: '#666', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px',
};
const smallBtnStyle = (ativo: boolean): React.CSSProperties => ({
  padding: '5px 12px', borderRadius: '16px', fontSize: '0.76rem', cursor: 'pointer',
  border: ativo ? '2px solid #03c9fe' : '1px solid #ddd', background: ativo ? '#eaf9ff' : '#fff', color: '#333',
});
const chipStyle = (ativo: boolean): React.CSSProperties => ({
  display: 'flex', alignItems: 'center', padding: '4px 10px', borderRadius: '16px',
  border: `1px solid ${ativo ? '#03c9fe' : '#ddd'}`, background: ativo ? '#e6f9ff' : '#fff',
  fontSize: '0.8rem', cursor: 'pointer',
});
const thStyle: React.CSSProperties = { textAlign: 'left', padding: '8px', borderBottom: '2px solid #eee', whiteSpace: 'nowrap' };
const tdStyle: React.CSSProperties = { padding: '6px 8px', borderBottom: '1px solid #f5f5f5', verticalAlign: 'top' };
const addCelulaBtnStyle: React.CSSProperties = {
  width: '20px', height: '20px', borderRadius: '50%', border: '1px dashed #bbb', background: '#fff',
  color: '#999', cursor: 'pointer', fontSize: '0.75rem', lineHeight: 1, padding: 0,
};
const avisoBoxStyle: React.CSSProperties = {
  display: 'flex', gap: '8px', alignItems: 'center', padding: '10px 12px', borderRadius: '8px',
  background: '#f8ecdc', color: '#b56a1f', fontSize: '0.8rem',
};
const iconToggleBtnStyle: React.CSSProperties = {
  flexShrink: 0, width: '30px', height: '30px', borderRadius: '50%', border: '1px solid #ddd',
  background: '#fff', color: '#666', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const matrizCardNormalStyle: React.CSSProperties = {
  background: '#fff', borderRadius: '10px', padding: '16px', marginBottom: '14px',
  boxShadow: '0 1px 4px rgba(0,0,0,0.08)', display: 'flex', flexDirection: 'column',
};
// A matriz é a tela mais reclamada por ficar pequena "sobrando espaço" ao
// redor — em tela cheia ela vira um cartão fixo cobrindo quase todo o
// viewport (24px de margem). O scroll de verdade agora é só da TABELA (ver
// tabelaScroll*Style) — o cartão em si não rola, senão o cabeçalho fixo da
// tabela não teria como funcionar (ficaria "fixo" em relação ao cartão, que
// por sua vez rolaria junto com tudo).
const matrizCardTelaCheiaStyle: React.CSSProperties = {
  position: 'fixed', top: '24px', left: '24px', right: '24px', bottom: '24px',
  background: '#fff', borderRadius: '12px', padding: '20px', zIndex: 1001,
  boxShadow: '0 12px 40px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column', overflow: 'hidden',
};
const telaCheiaBackdropStyle: React.CSSProperties = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
};
// Container que efetivamente rola (linhas/dias passam por dentro dele) —
// separado do cartão pra permitir cabeçalho da tabela com position:sticky.
// Em tela cheia ele ocupa o espaço restante do cartão (flex:1); no modo
// normal usa uma altura máxima fixa, já que o cartão ali cresce com a
// página normalmente (sem altura própria pra um flex:1 aproveitar).
const tabelaScrollNormalStyle: React.CSSProperties = {
  overflow: 'auto', maxHeight: '55vh',
};
const tabelaScrollTelaCheiaStyle: React.CSSProperties = {
  overflow: 'auto', flex: 1, minHeight: 0,
};
const thFixoStyle: React.CSSProperties = {
  ...thStyle, position: 'sticky', top: 0, background: '#fff', zIndex: 2, boxShadow: '0 1px 0 #eee',
};

export default ImportarFichaCampo;
